/**
 * Determines whether full borrower details should be visible for a loan
 * in the context of a given group view.
 *
 * Masking rules (confirmed decisions):
 *   - NULL group_id → show full details (backwards-compatible, legacy loans)
 *   - No viewing group (My Items context) → show full details
 *   - loan.group_id matches viewingGroupId → show full details
 *   - loan.group_id does NOT match → mask borrower identity
 * @param loan - The loan record containing an optional `group_id`. May be
 *   nullish, in which case the function returns `true`.
 * @param viewingGroupId - The group context the user is currently viewing.
 *   When omitted (e.g. the "My Items" screen), borrower details are always shown.
 * @returns `true` when full borrower details should be displayed; `false` when
 *   the borrower identity should be masked for cross-group privacy.
 */
export function canViewBorrowerDetails(
  loan: { group_id?: string | null } | null | undefined,
  viewingGroupId?: string,
): boolean {
  // No loan → nothing to mask
  if (!loan) return true;

  // No viewing group context (e.g. My Items) → always show
  if (!viewingGroupId) return true;

  // Legacy loan with no group_id → show full details
  if (!loan.group_id) return true;

  // Same group → show full details
  return loan.group_id === viewingGroupId;
}
